import { createCacheWrapper } from '@utils/cache';
import { DAYS_TO_MS, HOURS_TO_MS, configRefs } from '@utils/constants';
import { sitecoreAxios } from './core.axios.js';
import { AccessCodes } from './typings.access-codes.js';
import { buildUrl } from './util.build-url.js';

const cache = createCacheWrapper({
  timeToLiveMs: 1 * HOURS_TO_MS,
  evictAfterMs: 1 * DAYS_TO_MS
});
function getResourcesForPage(portalId, pageId, options) {
  const url = buildUrl(
    `/api/v1/portals/${portalId}/items/${pageId}/resourcesNavigation`,
    options
  );
  return cache(!(options == null ? void 0 : options.disableCache) ? url : void 0, async () => {
    const response = await sitecoreAxios.get(
      url,
      {
        responseType: "json"
      }
    );
    const result = response.data.items.map((item) => {
      switch (item.type) {
        case "Resource":
          return mapResource(item);
        case "ResourcePage":
          return mapResourcePage(item);
        case "ResourceGroup":
          return mapResourceGroup(item);
      }
    }).filter((it) => it !== void 0);
    return result;
  }).getStaleWhileRevalidate();
}
function mapResource(item) {
  const kind = (() => {
    switch (item.resource.type) {
      case "SimpleText":
      case "ComplexContent":
      case "Image":
      case "Audio":
      case "Video":
      case "Component":
      case "Ibook":
      case "Ibook3":
      case "Ibook4":
      case "Slider":
      case "TaskEngineLink":
        return "ContentElement";
      case "Document":
      case "ExternalLink":
      case "InternalLink":
        return item.resource.type;
      case "ResourcePage":
        return "Page";
      default:
        return "Page";
    }
  })();
  const thumbUrl = (() => {
    if (item.resource.videoThumb) {
      return `${configRefs.apis.video23.video.dangerouslyAssertSyncValue()}${item.resource.videoThumb}`;
    }
    if (item.resource.imageSrc) {
      return `${configRefs.apis.pm2.media.dangerouslyAssertSyncValue()}${item.resource.imageSrc}`;
    }
    return void 0;
  })();
  return {
    kind,
    id: item.id,
    accessCode: item.access ?? AccessCodes.ALLOWED,
    componentType: parseComponentType(item.resource.type),
    icon: item.iconSkin,
    src: item.resource.source,
    target: item.resource.target,
    title: item.title,
    card: {
      type: parseResourceCardType(item.iconSkin, item.resource.type),
      thumbUrl,
      metadata: {
        duration: item.resource.duration,
        author: item.resource.author,
        pageCount: item.resource.standardPageCount
      }
    }
  };
}
function mapResourcePage(item) {
  return {
    kind: "Page",
    id: item.id,
    accessCode: item.access ?? AccessCodes.ALLOWED,
    icon: item.iconSkin,
    src: item.id,
    target: item.target,
    title: item.title,
    card: {
      type: parseResourceCardType(item.iconSkin, void 0)
    }
  };
}
function mapResourceGroup(item) {
  if (!item.children) {
    return;
  }
  const children = item.children.map((child) => {
    switch (child.type) {
      case "Resource":
        return mapResource(child);
      case "ResourcePage":
        return mapResourcePage(child);
      default:
        return;
    }
  }).filter((child) => child !== void 0);
  if (!children.length) {
    return void 0;
  }
  return {
    kind: "Group",
    accessCode: item.access ?? AccessCodes.ALLOWED,
    id: item.id,
    icon: item.iconSkin,
    title: item.title,
    children
  };
}
function parseResourceCardType(iconSkin, fallbackType) {
  switch (iconSkin) {
    case "billede-icon":
      return "image";
    case "video-icon":
      return "video";
    case "digitalt-redskab-icon":
      return "component";
    case "dokument-icon":
      return "document";
    case "evaluering-icon":
      return "evaluation";
    case "link-icon":
      return "link";
    case "lyd-icon":
      return "audio";
    case "notesbog-icon":
      return "notebook";
    case "opslagsvaerk-icon":
      return "encyclopedia";
    case "tekst-icon":
      return "text";
    case "opgave-icon":
      return "task";
    case "til-laereren-icon":
      return "teacher";
    case "tekst-med-lyd-icon":
      return "text-with-audio";
    case "maal-icon":
      return "goal";
  }
  switch (fallbackType) {
    case "Audio":
      return "audio";
    case "Image":
      return "image";
    case "Video":
      return "video";
  }
}
function parseComponentType(componentType) {
  switch (componentType) {
    case "SimpleText":
      return "text";
    case "ComplexContent":
      return "richtext";
    case "Image":
      return "image";
    case "Audio":
      return "audio";
    case "Video":
      return "video";
    case "Component":
    case "Ibook":
    case "Ibook3":
    case "Ibook4":
    case "TaskEngineLink":
      return "iframe";
    case "Slider":
      return "image";
    default:
      return "unknown";
  }
}

export { getResourcesForPage };
//# sourceMappingURL=request.get-resources-for-page.js.map
